import { useEffect, useState, type ReactNode } from "react";
import {
  ArrowRight,
  CalendarDays,
  Check,
  Clock3,
  Menu,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import type { Service } from "./data";
import { formatMoney } from "./data";

const primaryNav = [
  { label: "Services", to: "/services" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export function ArrowLink({
  to,
  children,
  light = false,
}: {
  to: string;
  children: ReactNode;
  light?: boolean;
}) {
  return (
    <Link className={`arrow-link ${light ? "arrow-link--light" : ""}`} to={to}>
      {children}
      <ArrowRight size={17} strokeWidth={2.25} aria-hidden="true" />
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  return (
    <header className="site-header">
      <div className="page-shell header__inner">
        <Link className="brand" to="/" aria-label="Best One Cleans home">
          <span className="brand__mark"><Sparkles size={16} strokeWidth={2.3} /></span>
          <span>best<span>one</span><em>cleans</em></span>
        </Link>

        <nav className="desktop-nav" aria-label="Primary navigation">
          {primaryNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-link ${isActive ? "is-active" : ""}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="header__actions">
          <Link className="button button--small button--dark header__booking" to="/book">
            Book a clean <ArrowRight size={15} />
          </Link>
          <button
            className="menu-button"
            aria-label={open ? "Close navigation" : "Open navigation"}
            aria-expanded={open}
            aria-controls="site-mobile-navigation"
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="mobile-panel" id="site-mobile-navigation" role="dialog" aria-label="Site navigation">
          <nav className="page-shell mobile-nav" aria-label="Mobile navigation">
            {primaryNav.map((item, index) => (
              <NavLink key={item.to} to={item.to} className="mobile-nav__link">
              <span>{String(index + 1).padStart(2, "0")}</span>{item.label}<ArrowRight size={18} />
              </NavLink>
            ))}
            <Link className="button button--dark mobile-nav__cta" to="/book">Book your clean <ArrowRight size={17} /></Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="site-footer">
      <div className="page-shell footer__grid">
        <div className="footer__intro">
          <Link className="brand brand--light" to="/" aria-label="Best One Cleans home">
            <span className="brand__mark"><Sparkles size={16} strokeWidth={2.3} /></span>
            <span>best<span>one</span><em>cleans</em></span>
          </Link>
          <p>Thoughtful cleaning, booked around your day.</p>
          <Link className="button button--small button--mint" to="/book">Plan a clean <ArrowRight size={15} /></Link>
        </div>
        <div>
          <p className="footer__label">Explore</p>
          <Link to="/services">Services</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/about">How it works</Link>
          <Link to="/faq">Questions</Link>
        </div>
        <div>
          <p className="footer__label">For customers</p>
          <Link to="/book">Make a booking</Link>
          <Link to="/contact">Contact the team</Link>
        </div>
        <div className="footer__note">
          <p className="footer__label">A considered clean</p>
          <p>Tell us what matters in your home. We’ll shape the visit around the details.</p>
        </div>
      </div>
      <div className="page-shell footer__bottom">
        <span>© {new Date().getFullYear()} Best One Cleans</span>
        <span>Thoughtful cleaning, close to home.</span>
      </div>
    </footer>
  );
}

export function SectionIntro({
  eyebrow,
  title,
  copy,
  align = "left",
}: {
  eyebrow: string;
  title: ReactNode;
  copy?: ReactNode;
  align?: "left" | "center";
}) {
  return (
    <div className={`section-intro section-intro--${align}`}>
      <p className="eyebrow"><span />{eyebrow}</p>
      <h2>{title}</h2>
      {copy && <p className="section-intro__copy">{copy}</p>}
    </div>
  );
}

export function ServiceCard({ service, compact = false }: { service: Service; compact?: boolean }) {
  const Icon = service.icon;
  return (
    <article id={service.id} className={`service-card service-card--${service.tone} ${compact ? "service-card--compact" : ""}`}>
      <div className="service-card__top">
        <span className="service-card__icon"><Icon size={21} strokeWidth={1.8} /></span>
        <span className="service-card__price">from {formatMoney(service.startingAt)}</span>
      </div>
      <h3>{service.name}</h3>
      <p>{service.description}</p>
      {!compact && (
        <ul className="service-card__list">
          {service.bullets.map((bullet) => <li key={bullet}><Check size={15} />{bullet}</li>)}
        </ul>
      )}
      <div className="service-card__foot">
        <span><Clock3 size={14} />{service.duration}</span>
        <Link className="service-card__cta" to={`/book?service=${service.id}`}>Book this clean <ArrowRight size={17} /></Link>
      </div>
    </article>
  );
}

export function AssuranceRow() {
  return (
    <div className="assurance-row" aria-label="Booking benefits">
      <span><CalendarDays size={17} /> Choose a time that suits you</span>
      <span><ShieldCheck size={17} /> Confirmed before we arrive</span>
      <span><Sparkles size={17} /> Your priorities, your clean</span>
    </div>
  );
}
